<template>
  <div class="flex w-[1024px] flex-col">
    <div class="text-center">
      <div class="font-fredoka text-bg-sidebar text-lg">Your user ID:</div>

      <div class="mt-1 flex items-center justify-center gap-2">
        <div class="font-fredoka font-outline-1 text-2xl break-all text-white">
          {{ user?.uid ?? '' }}
        </div>

        <!-- 📋 Copy Button -->
        <button
          class="hover:text-bg-sidebar text-white transition"
          title="Copy to clipboard"
          @click="copyUid"
        >
          <Copy :size="20" />
        </button>
      </div>

      <div v-if="copied" class="text-bg-sidebar mt-1 text-xs">Copied!</div>
    </div>

    <div class="font-fredoka text-bg-sidebar text-center text-lg">Friends:</div>
    <div
      v-for="friend in friendsData"
      :key="friend.id"
      class="font-fredoka font-outline-1 flex items-center justify-center gap-2 text-center text-2xl break-all text-white"
    >
      <img
        v-if="friend.avatarUrl"
        :src="friend.avatarUrl"
        class="h-8 w-8 rounded-full"
        :alt="friend.name"
      />
      <span>{{ friend.name || friend.id }}</span>
      <!-- ❌ Remove Button -->
      <button
        class="ml-2 text-white transition hover:text-red-500"
        title="Remove friend"
        @click="removeFriend(friend.id)"
      >
        X
      </button>
    </div>
    <div v-if="isLoadingFriends" class="text-center text-white">Loading friends...</div>

    <div class="font-fredoka text-bg-sidebar mt-8 text-center text-lg">Add Friend:</div>
    <div class="flex justify-center">
      <div class="relative w-full max-w-md">
        <input
          v-model="friendId"
          placeholder="Friend ID"
          class="font-quicksand w-full resize-none rounded-xl border border-[#d8b99b] bg-[#fff7ef] p-4 pr-10 text-base text-[#5c3d2e] shadow-inner focus:ring-2 focus:ring-[#caa37b] focus:outline-none"
        />
        <!-- ✅ Checkmark Icon -->
        <div v-if="friendProfile" class="absolute top-1/2 right-3 -translate-y-1/2 text-green-600">
          ✅
        </div>
      </div>
      <!-- ➕ Add Button -->
      <button
        :disabled="!friendProfile"
        class="rounded-xl bg-[#caa37b] px-5 py-3 font-bold text-white shadow-md transition hover:bg-[#b98c66] disabled:cursor-not-allowed disabled:opacity-50"
        @click="addFriend"
      >
        Add
      </button>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { Copy } from 'lucide-vue-next';
import { useCurrentUser, useDocument } from 'vuefire';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { onMounted, ref, type Ref, watch, defineProps } from 'vue';
import { ensureRef } from '../composable/ensureRef.ts';
import QRCode from 'qrcode';
import { Profile } from '../models/Profile.ts';
import { userService, type UserData } from '../services/UserService.ts';

const friendId = ref('');
const friendProfile: Ref<Profile | null> = ref(null);
const qrUrl = ref('');
const db = getFirestore();
const user = useCurrentUser();
const document: Ref<any> = ref({});
const copied = ref(false);
const friendsData = ref<UserData[]>([]);
const isLoadingFriends = ref(false);

const props = defineProps<{
  friends: string[];
  onAdded: (id: string) => void;
  onRemoved?: (id: string) => void;
}>();

async function loadFriendsData() {
  if (props.friends.length === 0) {
    friendsData.value = [];
    return;
  }

  isLoadingFriends.value = true;
  try {
    friendsData.value = await userService.getUsersData(props.friends);
  } catch (error) {
    console.error('Error loading friends data:', error);
  } finally {
    isLoadingFriends.value = false;
  }
}

async function addFriend() {
  if (user.value == null || friendProfile.value == null) {
    return;
  }
  props.onAdded(friendId.value);
}

function removeFriend(id: string) {
  if (props.onRemoved) {
    props.onRemoved(id);
  }
}

onMounted(() => {
  ensureRef(user, async () => {
    qrUrl.value = await QRCode.toDataURL(user.value!.uid);
    const docRef = doc(db, `users/${user.value!.uid}`);
    document.value = useDocument(docRef);

    // Load friends data when component is mounted
    await loadFriendsData();
  });
});

// Watch for changes to the friends prop to reload data
watch(
  () => props.friends,
  async (_) => {
    await loadFriendsData();
  },
  { deep: true }
);

watch(friendId, async () => {
  if (friendId.value == user.value?.uid) {
    friendProfile.value = null;
    return;
  }
  const docRef = doc(db, `users/${friendId.value}/profile/default`);
  const docSnap = await getDoc(docRef);
  const friend: Profile | undefined = docSnap.data() as Profile;
  friendProfile.value = friend ?? null;
});

const copyUid = async () => {
  if (user.value?.uid) {
    await navigator.clipboard.writeText(user.value.uid);
    copied.value = true;

    setTimeout(() => (copied.value = false), 1500);
  }
};
</script>
